import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ExifReader from 'exifreader'; // Import the library
import { heicTo } from 'heic-to';
import ImageBlobReduce from 'image-blob-reduce';
import Pica from 'pica'
import { logoBase64 } from './logo';
import SignInButton from './SignInButton';
import CameraDetailsCard from './CameraDetailsCard';
import UploadStatusList from './UploadStatusList';
import ConfirmUploadDialog from './ConfirmUploadDialog';
import './App.css';
import { formatDate, generatePhotoPeriod } from './utils/dateHelpers';
import { normalizeExifDate, dateFromGps, parseDateFromFilename } from './utils/exifHelpers';
import AgeVerificationCard from './AgeVerificationCard';
import InvalidEventComponent from './InvalidEventComponent';
import { useWebHaptics } from 'web-haptics/react';
import {
  getPreferredLanguage,
  getLocaleForLanguage,
  formatNumberForLanguage,
  applyDocumentLanguage,
  translate,
  translatePlural
} from './i18n';
import { 
  auth, 
  signInWithCustomToken, 
  logoutUser, 
  uploadImageWithDetails, 
  readEpisode, 
  getSubmissionsByUID, 
  getStartDate, 
  getUserInfo, 
  readInstant, 
  checkAndIncrementEvent, 
  updateDateOfBirth,
  updateInstagramHandle,
  getDateOfBirth,
  createNewUser,
  getCameraRollCount
} from './firebase';

const pica = Pica({ features: ["js", "wasm", "cib"] });

function App() {
  const [lang] = useState(() => getPreferredLanguage(window.location.search));
  const locale = useMemo(() => getLocaleForLanguage(lang), [lang]);
  const t = useCallback((key, values = {}) => translate(lang, key, values), [lang]);
  const tPlural = useCallback((keyPrefix, count, values = {}) => translatePlural(lang, keyPrefix, count, values), [lang]);
  const formatCount = useCallback((value) => formatNumberForLanguage(lang, value), [lang]);
  const { trigger } = useWebHaptics();
  const haptic = useCallback(() => trigger([{ duration: 15 }], { intensity: 0.4 }), [trigger]);

  const [user, setUser] = useState({});
  const [authMethod, setAuthMethod] = useState(null); // Track the method of authentication
  const [cameraDetails, setCameraDetails] = useState({});
  const [uploadStatus, setUploadStatus] = useState({ completed: 0, message: '', errors: [], inProgress: false });
  const [isInvalidEvent, setIsInvalidEvent] = useState(false);
  const [noUploadsAllowed, setNoUploadsAllowed] = useState(false);
  const [showConfirmUploadDialog, setShowConfirmUploadDialog] = useState(false);
  const [uploadIssues, setUploadIssues] = useState({});
  const [version, setVersion] = useState(null);
  const [episodeData, setEpisodeData] = useState(null);

  // Possible values: 'INITIAL_LOADING', 'SIGN_IN_REQUIRED', 
  // 'VERIFICATION_DOB', 'VERIFICATION_INSTAGRAM', 'UNDER_AGE_LIMIT', 
  // 'READY', 'ERROR', 'NO_UPLOADS_ALLOWED', 'LOGGING_OUT'
  const [appStatus, setAppStatus] = useState('INITIAL_LOADING');
  const [isLoading, setIsLoading] = useState(true);
  const [birthDate, setBirthDate] = useState(null);

  const userRef = useRef(user);
  const authMethodRef = useRef(authMethod);
  const uploadDataRef = useRef(null);
  const handleConfirmUploadResponseRef = useRef(() => {});

  const fileInputRef = useRef(null);
  const messagesRef = useRef(null);

  const reducer = new ImageBlobReduce({pica});

  const resolveNativeCountTemplates = useCallback((value, replacements = {}) => {
    if (typeof value !== 'string') {
      return value;
    }

    return value
      .replace(/\\\((\w+)\)/g, (match, key) => {
        if (!(key in replacements)) {
          return match;
        }
        return String(replacements[key]);
      })
      .replace(/\{(\w+)\}/g, (match, key) => {
        if (!(key in replacements)) {
          return match;
        }
        return String(replacements[key]);
      });
  }, []);

  const parseDateSafely = useCallback((value) => {
    if (!value) return null;
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, []);

  useEffect(() => {
    applyDocumentLanguage(lang);
    if (typeof document !== 'undefined') {
      document.title = t('common.pageTitleUploadPhotos');
    }
  }, [lang, t]);

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    authMethodRef.current = authMethod;
  }, [authMethod]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Get the encoded timestamps string from the URL
    const encodedTimestamps = urlParams.get('timestamps');

    if (!encodedTimestamps) {
      return;
    }

    let timestamps = [];

    // Attempt to parse the JSON string to get the array of timestamps
    try {
      timestamps = JSON.parse(decodeURIComponent(encodedTimestamps));
    } catch (error) {
      console.error('Failed to parse timestamps:', error);
    }

    const allowAnyDateUploadsString = urlParams.get('allowAnyDateUploads');
    const allowAnyDateUploads = allowAnyDateUploadsString === 'true';
    const rawShotsRemaining = Number(urlParams.get('shotsRemaining'));
    const shotsRemaining = Number.isFinite(rawShotsRemaining) ? rawShotsRemaining : 0;
    const startDateParam = parseDateSafely(urlParams.get('startDate'));
    const endDateParam = parseDateSafely(urlParams.get('endDate'));
    const providedPhotoPeriod = urlParams.get('photoPeriod') || '';
    const fallbackPhotoPeriod = generatePhotoPeriod(
      startDateParam,
      endDateParam,
      shotsRemaining,
      allowAnyDateUploads,
      authMethodRef.current ?? 'token',
      locale,
      t
    );
    const formattedShotsRemaining = formatCount(shotsRemaining);
    const photoPeriod = fallbackPhotoPeriod || resolveNativeCountTemplates(providedPhotoPeriod, {
      count: formattedShotsRemaining,
      remaining: formattedShotsRemaining,
      shotsRemaining: formattedShotsRemaining
    });

    setUser({
      uid: urlParams.get('uid'),
      name: urlParams.get('fullName'),
    });

    setCameraDetails({
      name: urlParams.get('name'),
      startDate: urlParams.get('startDate'),
      endDate: urlParams.get('endDate'),
      formattedStartDate: urlParams.get('formattedStartDate'),
      formattedEndDate: urlParams.get('formattedEndDate'),
      allowAnyDateUploads: allowAnyDateUploads,
      photosPerPerson: urlParams.get('photosPerPerson'),
      shotsRemaining,
      photoPeriod,
      episodeID: urlParams.get('episodeID'),
      nodeID: urlParams.get('nodeID'),
      creator: urlParams.get('creator'),
      episodeTitle: urlParams.get('episodeTitle'),
      timestamps
    });
  }, [formatCount, locale, parseDateSafely, resolveNativeCountTemplates, t]);

  // Make sure fetchData is properly defined to handle loading of necessary data based on user details
  const fetchData = useCallback(async () => {
    setIsLoading(true);
  
    const eventID = new URLSearchParams(window.location.search).get('event');
    const instantID = new URLSearchParams(window.location.search).get('instant');
    
    if (!eventID && !instantID) {
      setIsInvalidEvent(true);
      setIsLoading(false);
      setAppStatus('ERROR');
      return;
    }
  
    let uid;
    const currentUser = userRef.current;
    if (currentUser && currentUser.uid) {
      uid = currentUser.uid;
    } else {
      const storedUserInfo = localStorage.getItem('userInfo');
      if (storedUserInfo) {
        const userInfo = JSON.parse(storedUserInfo);
        uid = userInfo.uid;
        setUser(userInfo);
      }
    }
  
    // Handle event tracking
    const currentAuthMethod = authMethodRef.current;
    if (currentAuthMethod !== 'token') {
      const eventIncrementedKey = `eventIncremented_${eventID || instantID}`;
      const eventIncremented = localStorage.getItem(eventIncrementedKey);
      if (!eventIncremented && uid) {
        checkAndIncrementEvent(currentUser, uid, eventID || instantID, !eventID);
      }
    }
  
    // Load episode data
    let episodeData, nodeID;
    try {
      if (instantID) {
        episodeData = await readInstant(instantID);
        nodeID = "1915A747-6CCE-43D6-84AF-3BF93DDDB131";
      } else {
        episodeData = await readEpisode(eventID);
        
        // Find the node ID
        const nodes = episodeData?.graph?.nodes ?? [];
        for (let node of nodes) {
          if (typeof node === 'object' && node.nodeType === 'povCamera') {
            nodeID = node.id;
            if (!episodeData.cameraDetails) {
              episodeData.cameraDetails = {
                name: episodeData.title,
                endDate: { seconds: node.attributes.endDate },
                photosPerPerson: node.attributes.numberOfShots,
              };
            }
            break;
          }
        }
        if (!nodeID) {
          nodeID = "1915A747-6CCE-43D6-84AF-3BF93DDDB131";
        }
      }
    
      // Handle missing or restricted episode data
      if (!episodeData) {
        setIsInvalidEvent(true);
        setIsLoading(false);
        setAppStatus('ERROR');
        return;
      }
      
      if (episodeData.cameraDetails?.allowCameraRollUploads === false) {
        setNoUploadsAllowed(true);
        setIsLoading(false);
        setAppStatus('NO_UPLOADS_ALLOWED');
        return;
      }
      
      // Store episode data first
      setEpisodeData(episodeData);
      
      // Process camera details
      const createdAt = new Date(episodeData.createdAt.seconds * 1000);
      let startDate = createdAt;
      let timestamps = [];
      let submissionsArray = [];
      let shotsRemaining = episodeData.cameraDetails.photosPerPerson;
      let endDate = episodeData.cameraDetails.endDate && !isHostFinish(episodeData.cameraDetails.endDate) 
        ? new Date(episodeData.cameraDetails.endDate.seconds * 1000) 
        : null;
      let formattedEndDate = endDate ? formatDate(endDate, false, false, locale, t) : null;
      const allowAnyDateUploads = episodeData.cameraDetails.allowAnyDateUploads ?? false;
      const episodeID = eventID || instantID;
      
      // Get user submissions if logged in
      if (uid) {
        sessionStorage.setItem(`nodeID-${episodeID}`, nodeID);
        startDate = await getStartDate(episodeID, nodeID);
        
        // Get camera roll count efficiently from user node
        const cameraRollCount = await getCameraRollCount(uid, episodeID, nodeID);
        shotsRemaining -= cameraRollCount;
        
        // Get submissions for duplicate timestamp checking
        const submissionsData = await getSubmissionsByUID(uid, episodeID, nodeID);
        if (submissionsData && Object.keys(submissionsData).length > 0) {
          submissionsArray = Object.values(submissionsData);
          timestamps = submissionsArray.map(submission => 
            new Date(submission.timestamp).toISOString());
        }
      }
      
      const formattedStartDate = startDate ? formatDate(startDate, false, false, locale, t) : null;
      const photoPeriod = generatePhotoPeriod(startDate, endDate, shotsRemaining, allowAnyDateUploads, authMethod, locale, t);
      const isHost = episodeData.creator === uid;
      
      // Set camera details
      setCameraDetails({
        ...formatData(episodeID, nodeID, episodeData),
        timestamps,
        shotsRemaining,
        photoPeriod,
        startDate,
        formattedStartDate,
        endDate,
        formattedEndDate,
        allowAnyDateUploads,
        episodeID,
        nodeID,
        isHost
      });
      
      // Handle age verification if needed
      if (authMethodRef.current === 'google' && 
          uid && 
          episodeData.cameraDetails?.ageVerificationLimit) {
                
        // Gather all possible date of birth sources
        let dateOfBirth = null;
        let hasDateOfBirth = false;
        
        // First check current user object
        if (userRef.current?.dateOfBirth) {
          dateOfBirth = new Date(userRef.current.dateOfBirth);
          hasDateOfBirth = true;
        }
        
        // Then check localStorage
        if (!hasDateOfBirth) {
          try {
            const localUserData = JSON.parse(localStorage.getItem('userInfo') || '{}');
            if (localUserData.dateOfBirth) {
              const storedDate = new Date(localUserData.dateOfBirth);
              if (!isNaN(storedDate.getTime())) {
                dateOfBirth = storedDate;
                hasDateOfBirth = true;
                
                // Update user state
                setUser(currentUser => ({
                  ...currentUser,
                  dateOfBirth: storedDate
                }));
              }
            }
          } catch (e) {
            console.warn("Error parsing localStorage user data:", e);
          }
        }
        
        // Finally check Firebase
        if (!hasDateOfBirth) {
          try {
            const firebaseDate = await getDateOfBirth(uid);
            if (firebaseDate) {
              dateOfBirth = new Date(firebaseDate);
              hasDateOfBirth = true;
              
              // Update user state and localStorage
              setUser(currentUser => ({
                ...currentUser,
                dateOfBirth: firebaseDate
              }));
              
              const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
              userInfo.dateOfBirth = firebaseDate;
              localStorage.setItem('userInfo', JSON.stringify(userInfo));
            }
          } catch (e) {
            console.warn("Error fetching date of birth from Firebase:", e);
          }
        }
        
        // Check if user is underage
        if (hasDateOfBirth) {
          const isUnderage = checkIfUserIsUnderAgeLimit(
            dateOfBirth, 
            episodeData.cameraDetails.ageVerificationLimit
          );
          
          if (isUnderage) {
            setIsLoading(false);
            setAppStatus('UNDER_AGE_LIMIT');
            return;
          }
          
          // User has DOB and is not underage - ready to show content
          setIsLoading(false);
          setAppStatus('READY');
          return;
        }
        
        // User needs to verify age
        setIsLoading(false);
        setAppStatus('VERIFICATION_DOB');
        return;
      }
      
      // No verification needed or token auth - ready to show content
      setIsLoading(false);
      setAppStatus('READY');
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsInvalidEvent(true);
      setIsLoading(false);
      setAppStatus('ERROR');
    }
  }, [authMethod, locale, t]);
  

  function isHostFinish(endDate) {
    return !endDate || isNaN(endDate.seconds);
  }

  const handleGoogleSignIn = useCallback(async (user) => {
    
    try {
      // Get user info from Firebase
      var userInfo = await getUserInfo(user.uid);
      
      // Check if userInfo has dateOfBirth, if not, try the direct helper
      if (userInfo && !userInfo.dateOfBirth) {
        const directDOB = await getDateOfBirth(user.uid);
        
        if (directDOB) {
          userInfo.dateOfBirth = directDOB;
        }
      }

      if (!userInfo) {
        console.log('Creating new user');
        userInfo = {
          uid: user.uid,
          name: user.displayName,
          photoURL: user.photoURL,
          // Add any other default fields you need
        };
        
        // Create the user in Firebase
        try {
          await createNewUser(userInfo); // You'll need to implement this in firebase.js
          console.log('New user created successfully');
        } catch (error) {
          console.error('Error creating new user:', error);
          throw error;
        }
      }
          
      // Store user info in localStorage
      localStorage.setItem('userInfo', JSON.stringify(userInfo));
      
      // Update state
      setUser(userInfo);
      setAuthMethod('google');
      
      // Fetch event data
      await fetchData();
      
      // Track event view if needed
      const urlParams = new URLSearchParams(window.location.search);
      const eventID = urlParams.get('event');
      const instantID = urlParams.get('instant');
      
      if (eventID || instantID) {
        checkAndIncrementEvent(user, user.uid, eventID ?? instantID, eventID == null);
      }
    } catch (error) {
      console.error("Error during sign-in process:", error);
    }
  }, [fetchData]);

  const signInWithToken = useCallback((token) => {
    signInWithCustomToken(auth, token)
      .then((result) => {
        setAuthMethod('token');
        return getUserInfo(result.user.uid);
      })
      .then(userInfo => {
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
        setUser(userInfo);
      })
      .catch(error => {
        console.error('Authentication error with token:', error);
                
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.unauthenticated) {
          window.webkit.messageHandlers.unauthenticated.postMessage('unauthenticated');
        }
      });
  }, []);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');

    if (!auth.currentUser && token) {
      setAuthMethod('token');
      // User is not logged in, and we have a token
      signInWithToken(token);
    }
  }, [signInWithToken]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const eventID = urlParams.get('event');
    const instantID = urlParams.get('instant');
        
    const unsubscribe = auth.onAuthStateChanged(async (userData) => {
      try {
        if (userData) {
          const newAuthMethod = token != null ? 'token' : 'google';
          setAuthMethod(newAuthMethod);
          
          // Step 1: Try to get user info from localStorage
          let storedUserInfo = null;
          
          try {
            const storedData = localStorage.getItem('userInfo');
            if (storedData) {
              storedUserInfo = JSON.parse(storedData);
            }
          } catch (e) {
            console.warn("Error reading from localStorage:", e);
          }
          
          // Step 2: Set user info
          if (newAuthMethod !== 'token') {
            if (!storedUserInfo) {
              // Fetch from server
              try {
                const freshUserInfo = await getUserInfo(userData.uid);
                
                if (freshUserInfo) {
                  setUser(freshUserInfo);
                  localStorage.setItem('userInfo', JSON.stringify(freshUserInfo));
                } else {
                  console.warn("getUserInfo returned null");
                  setUser(null);
                }
              } catch (error) {
                console.error("Error fetching user data:", error);
                setUser(null);
              }
            } else {
              // Use stored info
              setUser(storedUserInfo);
            }
            
            // Step 3: Fetch event data (now with unified loading)
            await fetchData();
          } else {
            // For token auth
            if (storedUserInfo) {
              setUser(storedUserInfo);
            }
          }
        } else {
          // No user is authenticated
          localStorage.removeItem('userInfo');
          setUser(null);
          
          const newAuthMethod = token != null ? 'token' : 'none';
          setAuthMethod(newAuthMethod);
          
          if (!token) {
            setIsInvalidEvent(!eventID && !instantID);
            await fetchData();
          }
        }
      } catch (e) {
        console.error("Error in auth state change handler:", e);
      }
    });
    
    return () => unsubscribe();
  }, [fetchData]);

  const formatData = (eventID, nodeID, episodeData) => {
    const { cameraDetails, createdAt } = episodeData;
    // Formatting the data for UI consumption
    const formattedData = {
      name: cameraDetails.name,
      createdAt: createdAt,
      webEndDate: episodeData.webEndDate, // Assuming 'webEndDate' is at the root of 'episodeData'
      photosPerPerson: cameraDetails.photosPerPerson,
      episodeID: eventID,
      nodeID: nodeID,
      creator: episodeData.creator,
      episodeTitle: episodeData.title
    };
  
    return formattedData;
  };

  useEffect(() => {
    function handleUpdate(message) {
      if (message.detail.type === 'cameraRollUpdated') {
        const rawShotsRemaining = Number(message.detail.shotsRemaining);

        setUploadStatus({ completed: 0, total: 0, message: '', inProgress: false, errors: [] });
        setCameraDetails(prevDetails => ({
          ...prevDetails,
          shotsRemaining: Number.isFinite(rawShotsRemaining)
            ? rawShotsRemaining
            : prevDetails.shotsRemaining,
          photoPeriod: (() => {
            const resolvedShotsRemaining = Number.isFinite(rawShotsRemaining)
              ? rawShotsRemaining
              : Number(prevDetails.shotsRemaining) || 0;
            const fallbackPhotoPeriod = generatePhotoPeriod(
              parseDateSafely(prevDetails.startDate),
              parseDateSafely(prevDetails.endDate),
              resolvedShotsRemaining,
              prevDetails.allowAnyDateUploads ?? false,
              authMethodRef.current ?? 'token',
              locale,
              t
            );
            const providedPhotoPeriod = message.detail.photoPeriod || '';
            const formattedShotsRemaining = formatCount(resolvedShotsRemaining);
            const resolvedPhotoPeriod = resolveNativeCountTemplates(providedPhotoPeriod, {
              count: formattedShotsRemaining,
              remaining: formattedShotsRemaining,
              shotsRemaining: formattedShotsRemaining
            });
            return fallbackPhotoPeriod || resolvedPhotoPeriod || prevDetails.photoPeriod;
          })(),
          timestamps: message.detail.timestamps
        }));
      }
    }

    function handleConfirmResponse(message) {
      if (message.detail.type === 'confirmUploadResponse') {
        handleConfirmUploadResponseRef.current(message.detail.response);
      }
    }
  
    window.addEventListener('cameraRollUpdated', handleUpdate);
    window.addEventListener('confirmUploadResponse', handleConfirmResponse);
  
    return () => {
      window.removeEventListener('cameraRollUpdated', handleUpdate);
      window.removeEventListener('confirmUploadResponse', handleConfirmResponse);
    };
  }, [formatCount, locale, parseDateSafely, resolveNativeCountTemplates, t]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const versionParam = parseInt(urlParams.get('version'), 10);
    const finalVersion = Number.isInteger(versionParam) ? versionParam : 1;
    setVersion(finalVersion);
  }, []);

  const debounce = (func, delay) => {
    let debounceTimer;
    return function() {
        const context = this;
        const args = arguments;
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(() => func.apply(context, args), delay);
    };
  };
  
  const handleButtonClick = useMemo(
    () => debounce(() => {
      const hasShotsRemaining = cameraDetails.shotsRemaining > 0;

      if (!uploadStatus.inProgress && hasShotsRemaining && fileInputRef.current) {
          fileInputRef.current.click();
      }
    }, 100),
    [cameraDetails.shotsRemaining, uploadStatus.inProgress]
  );

  const handleFileChange = async (event) => {
    const files = event.target.files;
    setUploadStatus(prev => ({ ...prev, errors: [], inProgress: true }));

    let processedData = await processFiles(files);
    processedData.fileInput = event.target;
    

    // Priority 1: Check for excess files first
    if (processedData.excessFiles > 0) {
      continueUpload(processedData); // This will show the "too many photos" error
      return;
    }
    
    const hasConfirmableIssues = (processedData.noDatePhotos > 0 || processedData.photosTakenBefore > 0 || processedData.photosTakenAfter > 0 || processedData.screenshots > 0);
    const hasGoodPhotos = processedData.successfulPhotos > 0;
    const hasOnlyPostErrors = !hasGoodPhotos && !hasConfirmableIssues && processedData.postUploadErrors.length > 0;
    
    // Priority 2: Handle confirmable issues
    if (hasConfirmableIssues && authMethod === 'google') {
      // Show ConfirmUploadDialog in web view for Google auth
      setUploadIssues({
        noDatePhotos: processedData.noDatePhotos,
        photosTakenBefore: processedData.photosTakenBefore,
        photosTakenAfter: processedData.photosTakenAfter,
        screenshots: processedData.screenshots,
        formattedStartDate: cameraDetails.formattedStartDate,
        formattedEndDate: cameraDetails.formattedEndDate
      });
      setShowConfirmUploadDialog(true);
      // Store essential data that won't change
      uploadDataRef.current = {
        ...processedData,
        episodeID: cameraDetails.episodeID,
        nodeID: cameraDetails.nodeID,
        creator: cameraDetails.creator,
        episodeTitle: cameraDetails.name,
        user: user,
        shotsRemaining: cameraDetails.shotsRemaining,
        version: version
      };
    }
    else if (hasConfirmableIssues && version >= 3) {
      // Send confirmation request to native bridge for App Clip v3+
      if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.confirmUpload) {
        const issueData = {
          noDatePhotos: processedData.noDatePhotos,
          photosTakenBefore: processedData.photosTakenBefore,
          photosTakenAfter: processedData.photosTakenAfter,
          screenshots: processedData.screenshots,
          formattedStartDate: cameraDetails.formattedStartDate,
          formattedEndDate: cameraDetails.formattedEndDate
        };
        uploadDataRef.current = {
          ...processedData,
          episodeID: cameraDetails.episodeID,
          nodeID: cameraDetails.nodeID,
          creator: cameraDetails.creator,
          episodeTitle: cameraDetails.name,
          user: user,
          shotsRemaining: cameraDetails.shotsRemaining,
          version: version
        };
        window.webkit.messageHandlers.confirmUpload.postMessage(JSON.stringify(issueData));
      } else {
        // Fallback if bridge not available
        continueUpload(processedData);
      }
    }
    else if (hasOnlyPostErrors && (authMethod === 'google' || version >= 3)) {
      // Only duplicates/invalid files - go straight to postUploadErrors
      if (processedData.fileInput) {
        processedData.fileInput.value = null;
      }
      
      setUploadStatus(prevStatus => ({ 
        ...prevStatus, 
        inProgress: false, 
        completed: 0, 
        errors: processedData.postUploadErrors.length > 0
          ? formatPostUploadErrors(processedData.postUploadErrors, cameraDetails.shotsRemaining, 0)
          : [t('upload.noValidPhotos')]
      }));
    }
    else {
      continueUpload(processedData);
    }
  };

  const processPhotoBlob = async (file, fileType) => {
    let blob = file;
    let needsCompression = true;
    
    if (fileType === 'image/heic') {
      try {
        // Convert HEIC to JPEG using heic-to library
        const jpegBlob = await heicTo({
          blob: file,
          type: "image/jpeg",
          quality: 0.75
        });
        blob = jpegBlob;
        // Successfully converted HEIC to JPEG, now we can compress it
      } catch (heicError) {
        console.error('HEIC conversion failed:', heicError);
        // If HEIC conversion fails, use the original file without compression
        // since the reducer can't handle HEIC files
        blob = file;
        needsCompression = false;
      }
    }
    
    // Apply compression only if it's not a failed HEIC file
    if (needsCompression) {
      try {
        blob = await reducer.toBlob(blob, {
          max: 1200,
          quality: 0.75
        });
      } catch (compressionError) {
        console.error('Image compression failed:', compressionError);
        // If compression fails, use the blob as is
      }
    }
    
    return blob;
  };

  const isReasonableDate = (date) => {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return false;
    const now = new Date();
    const minDate = new Date('1995-01-01');
    const maxDate = new Date(now.getFullYear() + 1, 11, 31); // Dec 31st of next year
    return date >= minDate && date <= maxDate;
  };

  const checkExcessFiles = (successfulPhotos, problematicQueueLength, shotsRemaining, currentIndex, totalFiles) => {
    const totalPotentialUploads = successfulPhotos + problematicQueueLength;
    
    // If no shots remaining, all files are excess
    if (shotsRemaining <= 0) {
      console.log(`[EXCESS FOUND] no shots remaining, all ${totalFiles} files are excess`);
      return totalFiles;
    }
    
    if (totalPotentialUploads > shotsRemaining) {
      const excess = totalPotentialUploads - shotsRemaining;
      console.log(`[EXCESS FOUND] returning ${excess} excess files`);
      return excess;
    }
    return 0;
  };

  const buildUploadHint = ({ remainingShots, allowAnyDateUploads, startLabel, endLabel }) => {
    if (remainingShots <= 0) {
      return t('upload.hint.deleteSomeFirst');
    }

    if (allowAnyDateUploads || !startLabel) {
      return remainingShots === 1
        ? t('upload.hint.selectBestPhoto')
        : t('upload.hint.selectBestPhotos', { count: formatCount(remainingShots) });
    }

    const countLabel = remainingShots === 1
      ? t('upload.onePhoto')
      : t('upload.upToPhotos', { count: formatCount(remainingShots) });

    if (endLabel) {
      return t('upload.hint.rangeBetween', {
        countLabel,
        start: startLabel,
        end: endLabel
      });
    }

    return t('upload.hint.rangeAfter', {
      countLabel,
      start: startLabel
    });
  };

  const processFiles = async (files) => {
    let successfulPhotos = 0;
    let excessFiles = 0;
    let newTimestamps = [];
    let newErrors = [];
    let postUploadErrors = []; // For Google auth: errors to show after upload
    let uploadQueue = [];
    let noDatePhotos = 0;
    let problematicPhotosQueue = [];
    const allowAnyDateUploads = cameraDetails.allowAnyDateUploads || !cameraDetails.startDate;
    let errorData = {
      noDatePhotosCount: 0,
      shotsRemaining: cameraDetails.shotsRemaining,
      allowAnyDateUploads,
      startLabel: cameraDetails.formattedStartDate,
      endLabel: cameraDetails.formattedEndDate
    };

    // Early check: if total files exceed shots remaining, return excess immediately
    if (files.length > cameraDetails.shotsRemaining) {
      const hint = buildUploadHint({
        remainingShots: cameraDetails.shotsRemaining,
        allowAnyDateUploads,
        startLabel: cameraDetails.formattedStartDate,
        endLabel: cameraDetails.formattedEndDate
      });
      const errorMessage = t('upload.tooManyPhotosSelected', { hint });
      
      return {
        successfulPhotos: 0,
        excessFiles: files.length - cameraDetails.shotsRemaining,
        newTimestamps: [],
        newErrors: [errorMessage],
        uploadQueue: [],
        noDatePhotos: 0,
        problematicPhotosQueue: [],
        photosTakenBefore: 0,
        photosTakenAfter: 0,
        duplicatePhotos: 0,
        screenshots: 0,
        postUploadErrors: [],
        errorData,
        total: files.length
      };
    }

    let index = 0;
    let duplicatePhotos = 0;
    let photosTakenBefore = 0;
    let photosTakenAfter = 0;
    let invalidFiletypePhotos = 0;
    let screenshots = 0;

    for (const file of files) {
      index++;
      const progressMessage = t('upload.processingPhotos', {
        current: formatCount(index),
        total: formatCount(files.length)
      });
      setUploadStatus(prev => ({
        ...prev,
        message: progressMessage,
        inProgress: true
      }));

      try {
        const fileType = file.type;
        let photoDate;

        if (fileType === 'image/png') {
          const pngData = await parsePNGData(file);
          const isScreenshot = detectIfScreenshots(pngData);

          if (isScreenshot) {
            // Check if it's a duplicate first (prioritize duplicate over screenshot)
            let candidateDate = findCreateDate(pngData);
            if (!candidateDate || !isReasonableDate(candidateDate)) {
              candidateDate = file.lastModified ? new Date(file.lastModified) : new Date();
              if (!isReasonableDate(candidateDate)) {
                candidateDate = new Date();
              }
            }
            photoDate = candidateDate;
            
            if (cameraDetails.timestamps.includes(photoDate.toISOString())) {
              duplicatePhotos++;
              continue; // Treat as duplicate, not screenshot
            }
            
            screenshots++;
            if (authMethod === 'google' || version >= 3) {
              // For Google auth or App Clip v3+, add screenshots to problematic queue with fallback date
              const processedBlob = await processPhotoBlob(file, fileType);
              problematicPhotosQueue.push({
                file: processedBlob,
                timestamp: photoDate.toISOString()
              });
              
              // Check if we've hit the limit
              excessFiles = checkExcessFiles(successfulPhotos, problematicPhotosQueue.length, cameraDetails.shotsRemaining, index, files.length);
              if (excessFiles > 0) break;
            }
            continue;  // Skip for token users, but processed for Google users
          }

          const candidateDate = findCreateDate(pngData);
          photoDate = (candidateDate && isReasonableDate(candidateDate)) ? candidateDate : null;

          if (!photoDate && (cameraDetails.isHost || allowAnyDateUploads)) {
            photoDate = new Date();
          }

          if (!photoDate) {
              const fallbackDate = file.lastModified ? new Date(file.lastModified) : new Date();
              const processedBlob = await processPhotoBlob(file, fileType);
              problematicPhotosQueue.push({
                file: processedBlob,
                timestamp: fallbackDate.toISOString()
              });
              noDatePhotos++;
              continue;
          }
        }
        else if (fileType === 'image/jpeg' || fileType === 'image/heic' || fileType === 'image/tiff') {
          const buffer = await file.arrayBuffer();
          const tags = ExifReader.load(buffer, { expanded: true });
          const candidateDate = normalizeExifDate(getExifDate(tags));
          photoDate = (candidateDate && isReasonableDate(candidateDate)) ? candidateDate : null;
                
          // exifr fallback
          if (!photoDate && fileType === 'image/jpeg') {
            try {
              // dynamic import keeps the main bundle lean
              const { parse } = await import('exifr');          // ←  loads only now
              const t = await parse(file, [
                'DateTimeOriginal',
                'DateTimeDigitized',
                'CreateDate',
                'GPSDateStamp',
                'GPSTimeStamp'
              ]);
        
              const candidateDate = normalizeExifDate(
                t?.DateTimeOriginal ?? t?.DateTimeDigitized ?? t?.CreateDate
              );
              photoDate = (candidateDate && isReasonableDate(candidateDate)) ? candidateDate : null;

              if (!photoDate && (t?.GPSDateStamp || t?.GPSTimeStamp)) {
                const gpsDate = dateFromGps(t.GPSDateStamp, t.GPSTimeStamp);
                photoDate = (gpsDate && isReasonableDate(gpsDate)) ? gpsDate : null;
              }
            } catch (err) {
              if (process.env.NODE_ENV !== 'production') {
                console.warn('[exifr fallback] failed:', err);
              }
            }
          }

          if (!photoDate) {
            const filenameDate = parseDateFromFilename(file.name);   // helper in utils section
            photoDate = (filenameDate && isReasonableDate(filenameDate)) ? filenameDate : null;
          }

          if (!photoDate && (cameraDetails.isHost || allowAnyDateUploads)) {
            const fallbackDate = file.lastModified ? new Date(file.lastModified) : new Date();
            photoDate = isReasonableDate(fallbackDate) ? fallbackDate : new Date();
          }
          
          if (!photoDate) {
            let fallbackDate = file.lastModified ? new Date(file.lastModified) : new Date();
            if (!isReasonableDate(fallbackDate)) {
              fallbackDate = new Date(); // Use current time if file.lastModified is unreasonable
            }
            const processedBlob = await processPhotoBlob(file, fileType);
            problematicPhotosQueue.push({
              file: processedBlob,
              timestamp: fallbackDate.toISOString()
            });
            noDatePhotos++;
            
            // Check if we've hit the limit
            excessFiles = checkExcessFiles(successfulPhotos, problematicPhotosQueue.length, cameraDetails.shotsRemaining, index, files.length);
            if (excessFiles > 0) break;
            continue;
          }
        }
        else {
          invalidFiletypePhotos++;
          continue;
        }

        const startDate = cameraDetails.startDate ? new Date(cameraDetails.startDate) : null;
        const endDate = cameraDetails.endDate ? new Date(cameraDetails.endDate) : null;

        // Check if the photo date is within the specified range
        if (cameraDetails.timestamps.includes(photoDate.toISOString())) {
          duplicatePhotos++;
        } else if (startDate && photoDate < startDate && !allowAnyDateUploads) {
            photosTakenBefore++;
            const processedBlob = await processPhotoBlob(file, fileType);
            problematicPhotosQueue.push({
              file: processedBlob,
              timestamp: photoDate.toISOString()
            });
            
            // Check if we've hit the limit
            excessFiles = checkExcessFiles(successfulPhotos, problematicPhotosQueue.length, cameraDetails.shotsRemaining, index, files.length);
            if (excessFiles > 0) break;
        } else if (endDate && photoDate > endDate && !allowAnyDateUploads) {
            photosTakenAfter++;
            const processedBlob = await processPhotoBlob(file, fileType);
            problematicPhotosQueue.push({
              file: processedBlob,
              timestamp: photoDate.toISOString()
            });
            
            // Check if we've hit the limit
            excessFiles = checkExcessFiles(successfulPhotos, problematicPhotosQueue.length, cameraDetails.shotsRemaining, index, files.length);
            if (excessFiles > 0) break;
        } else {
            const processedBlob = await processPhotoBlob(file, fileType);
            const newItem = {
              file: processedBlob,
              timestamp: photoDate.toISOString()
            };
           
            uploadQueue.push(newItem);
            successfulPhotos++;
            newTimestamps.push(photoDate.toISOString());

            // Check if we've hit the limit
            excessFiles = checkExcessFiles(successfulPhotos, problematicPhotosQueue.length, cameraDetails.shotsRemaining, index, files.length);
            if (excessFiles > 0) break;
        }
      } catch (error) {
        console.error('Error trying to parse file:', file.name, error);
        let fallbackDate = file.lastModified ? new Date(file.lastModified) : new Date();
        if (!isReasonableDate(fallbackDate)) {
          fallbackDate = new Date(); // Use current time if file.lastModified is unreasonable
        }
        
        try {
          const processedBlob = await processPhotoBlob(file, file.type);
          problematicPhotosQueue.push({
            file: processedBlob,
            timestamp: fallbackDate.toISOString()
          });
          noDatePhotos++;
          
          // Check if we've hit the limit
          excessFiles = checkExcessFiles(successfulPhotos, problematicPhotosQueue.length, cameraDetails.shotsRemaining, index, files.length);
          if (excessFiles > 0) break;
        } catch (processingError) {
          console.error('Error processing blob for file:', file.name, processingError);
          // Skip this file entirely if we can't process it at all
          invalidFiletypePhotos++;
        }
      }
    }

    errorData.noDatePhotosCount = noDatePhotos;

    // Generate error messages based on counts (except excess files - handled after recalculation)
    if (false) { // Temporarily disable this block - will be moved later
    }
    else {
      if (duplicatePhotos > 0) {
        const error = duplicatePhotos === 1
          ? t('upload.duplicatePhoto')
          : t('upload.duplicatePhotos');
        // For Google auth or App Clip v3+, store duplicate errors to show after upload
        if (authMethod === 'google' || version >= 3) {
          postUploadErrors.push(error);
        } else {
          newErrors.push(error);
        }
      }
  
      if (screenshots > 0) {
        const error = t('upload.screenshotsNotAllowed');
        // For Google auth or App Clip v3+, screenshots will be mentioned in the ConfirmUploadDialog
        if (authMethod !== 'google' && version < 3) {
          newErrors.push(error);
        }
      }

      const validRange = getValidRangeString();
  
      if (authMethod !== 'google' && version < 3 && photosTakenBefore > 0) {
        const error = photosTakenBefore === 1
          ? t('upload.photoTakenBeforeEventStart', { validRange })
          : t('upload.photosTakenBeforeDate', {
            count: formatCount(photosTakenBefore),
            date: cameraDetails.formattedStartDate,
            validRange
          });
        newErrors.push(error);
      }
  
      if (authMethod !== 'google' && version < 3 && photosTakenAfter > 0) {
        const error = photosTakenAfter === 1
          ? t('upload.photoTakenAfterEventEnd', { validRange })
          : t('upload.photosTakenAfterDate', {
            count: formatCount(photosTakenAfter),
            date: cameraDetails.formattedEndDate,
            validRange
          });
        newErrors.push(error);
      }

      if (authMethod !== 'google' && version < 3 && noDatePhotos > 0) {
        const noDatesErrorString = getNoDatesErrorString(errorData);
        newErrors.push(noDatesErrorString);
      }
  
      if (invalidFiletypePhotos > 0) {
        const updatedShotsRemaining = cameraDetails.shotsRemaining - successfulPhotos;
        const hint = buildUploadHint({
          remainingShots: updatedShotsRemaining,
          allowAnyDateUploads,
          startLabel: cameraDetails.formattedStartDate,
          endLabel: cameraDetails.formattedEndDate
        });

        const error = invalidFiletypePhotos === 1
          ? t('upload.invalidTypePhoto', { hint })
          : t('upload.invalidTypePhotos', {
            count: formatCount(invalidFiletypePhotos),
            hint
          });
        // For Google auth or App Clip v3+, store invalid file type data to calculate error message later
        if (authMethod === 'google' || version >= 3) {
          // Store the error data to calculate message later with correct shots remaining
          postUploadErrors.push({
            type: 'invalidFiletype',
            count: invalidFiletypePhotos,
            allowAnyDateUploads,
            startLabel: cameraDetails.formattedStartDate,
            endLabel: cameraDetails.formattedEndDate
          });
        } else {
          newErrors.push(error);
        }
      }
    }
  
    
    // Generate excess files error message AFTER recalculation
    if (excessFiles > 0) {
      const updatedShotsRemaining = cameraDetails.shotsRemaining - successfulPhotos;
      const hint = buildUploadHint({
        remainingShots: updatedShotsRemaining,
        allowAnyDateUploads,
        startLabel: cameraDetails.formattedStartDate,
        endLabel: cameraDetails.formattedEndDate
      });
      const errorMessage = t('upload.tooManyPhotosSelected', { hint });
      newErrors.push(errorMessage);
    }
  
    return {
      successfulPhotos,
      excessFiles,
      newTimestamps,
      newErrors,
      uploadQueue,
      noDatePhotos,
      problematicPhotosQueue,
      photosTakenBefore,
      photosTakenAfter,
      duplicatePhotos,
      screenshots,
      postUploadErrors,
      errorData,
      total: files.length
    };
  };

  const handleConfirmUploadResponse = (userChoice) => {
    setShowConfirmUploadDialog(false);

    const uploadData = uploadDataRef.current;
    
    
    if (userChoice === 'yes') {
      // Process ALL photos: good ones + all problematic ones
      uploadData.successfulPhotos += uploadData.problematicPhotosQueue.length;
      uploadData.newTimestamps = [...uploadData.newTimestamps, ...uploadData.problematicPhotosQueue.map(p => p.timestamp)];
      uploadData.uploadQueue = [...uploadData.uploadQueue, ...uploadData.problematicPhotosQueue];
  
      continueUpload(uploadData);
    } else {
      // Only process good photos if any exist
      const hasGoodPhotos = uploadData.successfulPhotos > 0;
      
      if (hasGoodPhotos) {
        continueUpload(uploadData);
      } else {
        // No good photos - show postUploadErrors if they exist, otherwise generic message
        if (uploadData.fileInput) {
          uploadData.fileInput.value = null;
        }

        // For Google auth or App Clip v3+, show specific errors (duplicates, invalid files) instead of generic message
        const errorsToShow = ((authMethod === 'google' || version >= 3) && uploadData.postUploadErrors?.length > 0) 
          ? formatPostUploadErrors(uploadData.postUploadErrors, uploadData.shotsRemaining || cameraDetails.shotsRemaining, 0)
          : [t('upload.noValidPhotos')];

        setUploadStatus(prevStatus => ({ 
          ...prevStatus, 
          inProgress: false, 
          completed: 0, 
          errors: errorsToShow
        }));
      }
    }
  
    uploadDataRef.current = null;
  };

  handleConfirmUploadResponseRef.current = handleConfirmUploadResponse;


  const getValidRangeString = () => {
    if (cameraDetails.startDate && cameraDetails.endDate) {
      return t('upload.rangeBetweenAccepted', {
        start: cameraDetails.formattedStartDate,
        end: cameraDetails.formattedEndDate
      });
    } else if (cameraDetails.startDate) {
      return t('upload.rangeAfterAccepted', {
        start: cameraDetails.formattedStartDate
      });
    } else {
      return t('upload.willBeAccepted');
    }
  };

  const getNoDatesErrorString = (errorData) => {
    const { noDatePhotosCount, shotsRemaining, allowAnyDateUploads, startLabel, endLabel } = errorData;
    const hint = buildUploadHint({
      remainingShots: shotsRemaining,
      allowAnyDateUploads,
      startLabel,
      endLabel
    });
    return noDatePhotosCount === 1
      ? t('upload.photoNoDate', { hint })
      : t('upload.photosNoDate', {
        count: formatCount(noDatePhotosCount),
        hint
      });
  }

  const formatPostUploadErrors = (postUploadErrors, originalShotsRemaining, successfulUploads) => {
    if (!postUploadErrors.length) return [];
    
    return postUploadErrors.map(errorData => {
      if (typeof errorData === 'string') {
        return errorData;
      }
      
      if (errorData.type === 'invalidFiletype') {
        const updatedShotsRemaining = Math.max(0, originalShotsRemaining - successfulUploads);
        const hint = buildUploadHint({
          remainingShots: updatedShotsRemaining,
          allowAnyDateUploads: errorData.allowAnyDateUploads,
          startLabel: errorData.startLabel,
          endLabel: errorData.endLabel
        });
        
        if (errorData.count === 1) {
          return t('upload.invalidTypePhoto', { hint });
        }
        return t('upload.invalidTypePhotos', {
          count: formatCount(errorData.count),
          hint
        });
      }
      
      return String(errorData);
    });
  }

  const continueUpload = (uploadData) => {
    const { successfulPhotos, excessFiles, newTimestamps, newErrors, postUploadErrors = [], uploadQueue, total } = uploadData;
  
    if (excessFiles > 0) {
      // Don't upload anything when there are excess files - just show the error
      if (uploadData.fileInput) {
        uploadData.fileInput.value = null;
      }
      setUploadStatus(prevStatus => ({ 
        ...prevStatus, 
        inProgress: false, 
        completed: 0, 
        message: '', // Clear any upload message
        errors: newErrors // newErrors should contain the "Too many photos selected" message
      }));
    } else if (successfulPhotos > 0) {
      // Use stored shotsRemaining if available, otherwise use current cameraDetails
      const currentShotsRemaining = uploadData?.shotsRemaining ?? cameraDetails.shotsRemaining;
      
      const expectedRemainingAfterUpload = currentShotsRemaining - successfulPhotos;
      
      setCameraDetails(prevDetails => {
        const newShotsRemaining = currentShotsRemaining - successfulPhotos;
        return {
          ...prevDetails,
          shotsRemaining: newShotsRemaining,
          timestamps: [...prevDetails.timestamps, ...newTimestamps]
        };
      });
      // For Google auth or App Clip v3+, combine regular errors with post-upload errors (duplicates, invalid file types)
      const formattedPostUploadErrors = formatPostUploadErrors(postUploadErrors, currentShotsRemaining, successfulPhotos);
      const allErrors = (authMethod === 'google' || version >= 3) ? [...new Set([...newErrors, ...formattedPostUploadErrors])] : newErrors;
      setUploadStatus({ completed: 0, message: t('upload.uploadingPhotos'), inProgress: true, errors: allErrors });
      uploadAllImages(uploadQueue, allErrors, total, { ...uploadData, expectedRemainingAfterUpload }).then(results => {
        if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.uploadComplete) {
          window.webkit.messageHandlers.uploadComplete.postMessage(JSON.stringify({
            successfulUploads: results.successfulUploads,
            errors: results.errors
          }));
        }
      });
    } else {
      // For Google auth or App Clip v3+, show postUploadErrors if they exist, otherwise show generic message
      let errorsToShow;
      if ((authMethod === 'google' || version >= 3) && postUploadErrors.length > 0) {
        const formattedPostUploadErrors = formatPostUploadErrors(postUploadErrors, cameraDetails.shotsRemaining, 0);
        errorsToShow = [...new Set([...newErrors, ...formattedPostUploadErrors])];
      } else if (newErrors.length > 0) {
        errorsToShow = newErrors;
      } else {
        errorsToShow = [t('upload.noValidPhotos')];
      }
      
      // Reset file input
      if (uploadData.fileInput) {
        uploadData.fileInput.value = null;
      }
      
      setUploadStatus(prevStatus => ({ ...prevStatus, inProgress: false, completed: 0, errors: errorsToShow }));
    }
  };
    
  const handleLogout = () => {
    // Set app status to logging out first
    setAppStatus('LOGGING_OUT');
    localStorage.removeItem('userInfo');
    
    logoutUser().then(() => {
      // Reset user state
      setUser(null);
      setAuthMethod('none');
      
      const hasToken = new URLSearchParams(window.location.search).get('token') != null;
      
      if (hasToken) {
        window.location.reload();
      } else {
        // For Google auth, transition to sign in required
        setAppStatus('SIGN_IN_REQUIRED');
      }
    }).catch(error => {
      console.error('Error during logout:', error);
      setAppStatus('SIGN_IN_REQUIRED');
    });
  };

  const checkIfUserIsUnderAgeLimit = (dateOfBirth, ageLimit) => {
    if (!dateOfBirth || !ageLimit) return false;
    
    // Make sure we have a Date object
    const birthDate = dateOfBirth instanceof Date ? dateOfBirth : new Date(dateOfBirth);
    
    // Check if the date is valid
    if (isNaN(birthDate.getTime())) return false;
    
    // Calculate age
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    
    // Adjust age if birthday hasn't occurred yet this year
    if (
      today.getMonth() < birthDate.getMonth() || 
      (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    
    // Return true if user is under the age limit
    return age < ageLimit;
  };  
  
  const handleAgeVerificationComplete = async (dateOfBirth, instagram, step) => {
    try {
      setIsLoading(true);
      
      // 1) Update parent's confirmed DOB state so it's not lost
      setBirthDate(dateOfBirth);
      
      // 2) Also update your user object, Firebase, localStorage, etc.
      const updatedUser = {
        ...user,
        dateOfBirth,
      };
      await updateDateOfBirth(dateOfBirth, episodeData.id, episodeData.creator);
      
      if (step === 'instagram' && instagram) {
        await updateInstagramHandle(instagram, episodeData.id);
        updatedUser.instagramHandle = instagram;
      }
      setUser(updatedUser);
      localStorage.setItem('userInfo', JSON.stringify(updatedUser));
      
      // 3) Check if underage
      const isUnderage = checkIfUserIsUnderAgeLimit(dateOfBirth, episodeData.cameraDetails.ageVerificationLimit);
      if (isUnderage) {
        setIsLoading(false);
        setAppStatus('UNDER_AGE_LIMIT');
        return;
      }
      
      // 4) Move to next step
      if (step === 'instagram') {
        setIsLoading(false);
        setAppStatus('READY');
      } else if (step === 'dob') {
        setIsLoading(false);
        setAppStatus('VERIFICATION_INSTAGRAM');
      }
    } catch (error) {
      console.error('Error in age verification flow:', error);
      setIsLoading(false);
      // Keep current status, but show an error if needed
    }
  };  


  const uploadImage = async (image, index, total, uploadData = null) => {
    try {
      
      const timestamp = (new Date(image.timestamp)).getTime();
      
      // Use stored data if available, otherwise fall back to cameraDetails/user
      const episodeID = uploadData?.episodeID || cameraDetails.episodeID;
      const nodeID = uploadData?.nodeID || cameraDetails.nodeID;
      const creator = uploadData?.creator || cameraDetails.creator;
      const episodeTitle = uploadData?.episodeTitle || cameraDetails.name;
      const userData = uploadData?.user || user;
      
      await uploadImageWithDetails(
        image.file,
        timestamp,
        userData,
        episodeID,
        nodeID,
        creator,
        episodeTitle
      );

      setUploadStatus(prev => ({
        ...prev,
        completed: prev.completed + 1,
        message: t('upload.progressPhotosUploaded', {
          current: formatCount(prev.completed + 1),
          total: formatCount(total)
        })
      }));
      return { success: true };
    } catch (error) {
      console.error(`Error uploading image ${index + 1}:`, error);
      return { success: false, error: error };
    }
  };
  
  const uploadAllImages = async (uploadQueue, validationErrors, total, uploadData = null) => {
    const uploadPromises = uploadQueue.map((image, index) =>
        uploadImage(image, index, uploadQueue.length, uploadData)
    );

    const results = await Promise.all(uploadPromises);
    const errors = results.filter(result => !result.success);
    const successfulUploads = results.length - errors.length;

    // For version >= 3, treat any validation errors as upload errors to match Google auth flow
    const storedVersion = uploadData?.version ?? version;
    const hasValidationErrors = validationErrors.length > 0;
    
    // Check if App Clip has any problematic photos that should show errors
    // Note: For App Clip v3+, screenshots/dates are handled via dialog, only duplicates and invalid files are post-upload errors
    const hasAppClipErrors = storedVersion >= 3 && uploadData && (
      (uploadData.postUploadErrors?.length || 0) > 0
    );
    
    const shouldUseErrorFlow = errors.length > 0 || hasValidationErrors || hasAppClipErrors;

    if (shouldUseErrorFlow) {
      let newErrors = [
        ...validationErrors,
        ...errors.map((result, index) => t('upload.errorInImage', {
          index: formatCount(index + 1),
          message: result.error.message
        }))
      ];
      
      // Generate error messages for App Clip from uploadData
      if (hasAppClipErrors && validationErrors.length === 0) {
        const appClipErrors = [];
        // Only add postUploadErrors (duplicates and invalid file types)
        if (uploadData.postUploadErrors?.length > 0) {
          const originalShotsRemaining = uploadData.shotsRemaining || cameraDetails.shotsRemaining;
          const formattedErrors = formatPostUploadErrors(uploadData.postUploadErrors, originalShotsRemaining, successfulUploads);
          appClipErrors.push(...formattedErrors);
        }
        newErrors = [...newErrors, ...appClipErrors];
      }
      
      
      setUploadStatus(prev => ({
          ...prev,
          message: t('upload.uploadedSummary', {
            successful: formatCount(successfulUploads),
            total: formatCount(total)
          }),
          errors: newErrors,
          inProgress: false
      }));
    } else {
      // Calculate remaining shots after this upload
      let remainingAfterUpload;
      if (uploadData?.expectedRemainingAfterUpload !== undefined) {
        remainingAfterUpload = uploadData.expectedRemainingAfterUpload;
      } else {
        const currentRemaining = Number(cameraDetails.shotsRemaining) || 0;
        remainingAfterUpload = currentRemaining - successfulUploads;
      }
      
      const formattedRemaining = formatCount(Math.max(0, remainingAfterUpload));
      const formattedSuccessfulUploads = formatCount(successfulUploads);
      const message = remainingAfterUpload <= 0
        ? t('upload.usedAllShotsTap')
        : (successfulUploads === 1
          ? resolveNativeCountTemplates(
            t('upload.successUploadedOne', {
              remaining: formattedRemaining,
              count: formattedRemaining
            }),
            {
              count: formattedRemaining,
              remaining: formattedRemaining,
              shotsRemaining: formattedRemaining
            }
          )
          : resolveNativeCountTemplates(
            t('upload.successUploadedMany', {
              count: formattedSuccessfulUploads,
              remaining: formattedRemaining
            }),
            {
              count: formattedSuccessfulUploads,
              remaining: formattedRemaining,
              shotsRemaining: formattedRemaining
            }
          ));
      
      setUploadStatus(prev => ({
          ...prev,
          message: message,
          errors: [],
          inProgress: false
      }));
    }

    return {
        successfulUploads,
        errors
    };
  };

  const parsePNGData = async (pngFile) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const buffer = e.target.result;
            const view = new DataView(buffer);
            let pos = 8;  // Start after the 8-byte PNG signature
            let metadataTexts = [];

            while (pos < buffer.byteLength) {
                const length = view.getUint32(pos);
                const type = String.fromCharCode(
                    view.getUint8(pos + 4),
                    view.getUint8(pos + 5),
                    view.getUint8(pos + 6),
                    view.getUint8(pos + 7)
                );

                if (type === 'IEND') break;

                if (type === 'tEXt' || type === 'zTXt' || type === 'iTXt') {
                    const dataStart = pos + 8;
                    const dataEnd = dataStart + length;
                    const data = new TextDecoder('utf-8').decode(new Uint8Array(buffer.slice(dataStart, dataEnd)));
                    metadataTexts.push(data);
                }

                pos += length + 12;  // Move to the next chunk
            }

            resolve(metadataTexts);
        };

        reader.onerror = (e) => reject(e);
        reader.readAsArrayBuffer(pngFile);
    });
  };

  const detectIfScreenshots = (pngData) => {
    return pngData.some(text => text.includes('Screenshot') || text.includes('Screen Shot'));
  };

  const findCreateDate = (pngData) => {
    for (const xmlString of pngData) {
        if (xmlString.includes('CreateDate="')) {
            const parts = xmlString.split('CreateDate="');
            if (parts.length > 1) {
                const date = parts[1].split('"')[0];
                return new Date(date); // Return the extracted date
            }
        }
    }

    return null;
  };

  // Helper function to extract date from EXIF tags
  const getExifDate = (tags) => {
    // flat form (expanded: false)
    if (tags.DateTimeOriginal)   return tags.DateTimeOriginal.description;
    if (tags.DateTimeDigitized)  return tags.DateTimeDigitized.description;
    if (tags.DateTime)           return tags.DateTime.description;
    if (tags.CreateDate)         return tags.CreateDate.description;
  
    // nested form (expanded: true)
    if (tags.exif) {
      const ex = tags.exif;
      if (ex.DateTimeOriginal)   return ex.DateTimeOriginal.description;
      if (ex.DateTimeDigitized)  return ex.DateTimeDigitized.description;
      if (ex.DateTime)           return ex.DateTime.description;
      if (ex.CreateDate)         return ex.CreateDate.description;
    }
    return null;
  };

  if (noUploadsAllowed) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-theme-bg">
        <img src={require('./images/noUploadsAllowed.png')} alt={t('common.noUploadsAllowedAlt')} className="max-w-32 pb-3" />
        <div className="font-satoshi font-medium text-lg text-theme-white-100">
          {t('upload.hostDisabledUploads')} 😕
        </div>
      </div>
    );
  }

  const hasToken = new URLSearchParams(window.location.search).get('token') != null;

  if (appStatus === 'INITIAL_LOADING' && (!hasToken || !auth.currentUser)) {
    const colorName = version >= 2 ? ' bg-theme-v2-clip-bg' : (hasToken ? '' : ' bg-theme-bg');
    const loadingClassName = `flex flex-col items-center justify-center min-h-screen${colorName}`
    return (
      <div className={loadingClassName}>
        <div className="font-satoshi font-medium text-theme-white-50">{t('common.loading')}</div>
      </div>
    );
  }

  if (isInvalidEvent) {
    return <InvalidEventComponent authMethod={authMethod} t={t} />;
  }

  if ((!user || Object.keys(user).length === 0) && authMethod !== 'token' && appStatus !== 'INITIAL_LOADING' && !isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-theme-bg p-px">
        <div className="flex flex-col items-center justify-center pb-8 w-full max-w-[800px]">
          <img src={logoBase64} alt={t('common.povLogoAlt')} className='max-w-16 pb-3'/>
          <p className='font-satoshi font-medium text-lg text-theme-white-100 pb-1'>{t('auth.signInToUploadPhotos')}</p>
          <p className='font-satoshi font-medium text-md text-theme-white-50'>{cameraDetails.name || t('common.loadingEvent')}</p>
        </div>
        <div className="flex flex-col items-center justify-center w-full max-w-[800px]">
          <SignInButton onSignIn={handleGoogleSignIn} t={t} haptic={haptic} />
        </div>
      </div>
    );
  }

  const translucentBg = version >= 2 ? 'bg-theme-v2-translucent' : 'bg-theme-v1-translucent';
  const translucentBorder = version >= 2 ? 'from-theme-v2-gradient-translucent-top to-theme-v2-gradient-translucent-bottom' : 'from-theme-v1-gradient-translucent-top to-theme-v1-gradient-translucent-bottom';
  const isActionable = cameraDetails.shotsRemaining > 0 && !uploadStatus.inProgress;
  const hasUploadedShots = cameraDetails.shotsRemaining < cameraDetails.photosPerPerson;
  const buttonColorName = isActionable ? (hasUploadedShots ? `${translucentBg} text-theme-white-100` : 'bg-theme-purple-fill text-theme-white-100') : 'bg-theme-purple-disabled text-theme-white-50';
  const activeName = isActionable ? 'active:opacity-70' : '';
  const gradientName = isActionable ? (hasUploadedShots ? translucentBorder : 'from-theme-gradient-purple-top to-theme-gradient-purple-bottom') : 'from-theme-gradient-disabled-top to-theme-gradient-disabled-bottom';

  let buttonText;

  if (uploadStatus.inProgress) {
    buttonText = t('common.loading');
  }
  else if (uploadStatus.errors.length > 0) {
    buttonText = t('common.tryAgain');
  }
  else if (cameraDetails.shotsRemaining === 0 || !hasUploadedShots) {
    buttonText = t('upload.addShots');
  }
  else {
    buttonText = t('upload.addMoreShots');
  }

  const buttonClassName = `${buttonColorName} font-satoshi font-bold text-[17px] w-full h-[56px] py-3 px-4 rounded-[10px] ${activeName} select-none`;
  const outlineClassName = `rounded-[10px] bg-gradient-to-b ${gradientName} mt-4 p-[1px]`;

   // Depending on the auth method, render different components
   const renderMainComponent = () => {    
    // Always show loading first if isLoading is true
    if (isLoading || appStatus === 'LOGGING_OUT') {
      return (
        <div className="flex flex-col items-center justify-center min-h-screen bg-theme-bg">
            <div className="font-satoshi font-medium text-theme-white-50">
            {appStatus === 'LOGGING_OUT' ? t('common.signingOut') : t('common.loading')}
          </div>
        </div>
      );
    }
    
    // Then handle all possible app states
    switch (appStatus) {
      case 'SIGN_IN_REQUIRED':
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-theme-bg p-px">
            <div className="flex flex-col items-center justify-center pb-8 w-full max-w-[800px]">
              <img src={logoBase64} alt={t('common.povLogoAlt')} className='max-w-16 pb-3'/>
              <p className='font-satoshi font-medium text-lg text-theme-white-100 pb-1'>{t('auth.signInToUploadPhotos')}</p>
              <p className='font-satoshi font-medium text-md text-theme-white-50'>{cameraDetails.name || t('common.loadingEvent')}</p>
            </div>
            <div className="flex flex-col items-center justify-center w-full max-w-[800px]">
              <SignInButton onSignIn={handleGoogleSignIn} t={t} haptic={haptic} />
            </div>
          </div>
        );

      case 'VERIFICATION_DOB':
      case 'VERIFICATION_INSTAGRAM':
      case 'UNDER_AGE_LIMIT':
        if (episodeData) {
          return (
            <AgeVerificationCard
              isOpen={true}
              onClose={() => setAppStatus('READY')}
              episode={episodeData}
              hostName={episodeData?.creatorUsername}
              onConfirm={handleAgeVerificationComplete}
              user={user}
              handleLogout={handleLogout}
              t={t}
              lang={lang}
              initialStep={
                appStatus === 'VERIFICATION_DOB' ? 'dob' : 
                appStatus === 'VERIFICATION_INSTAGRAM' ? 'instagram' : 
                appStatus === 'UNDER_AGE_LIMIT' ? 'underAgeLimit' : null
              }
              birthDate={birthDate}
              haptic={haptic}
            />
          );
        }
        return null;
        
      case 'READY':
        return (
          <CameraDetailsCard
            user={user}
            cameraDetails={cameraDetails}
            buttonText={buttonText}
            buttonClassName={buttonClassName}
            fileInputRef={fileInputRef}
            handleFileChange={handleFileChange} 
            handleButtonClick={handleButtonClick}
            handleLogout={handleLogout}
            uploadStatus={uploadStatus}
            t={t}
            formatCount={formatCount}
            haptic={haptic}
          />
        );

      case 'ERROR':
      case 'INITIAL_LOADING':
      default:
        return (
          <div className="flex flex-col items-center justify-center min-h-screen bg-theme-bg">
            <div className="font-satoshi font-medium text-theme-white-50">
              {appStatus === 'ERROR' ? t('common.errorLoadingEvent') : t('common.loading')}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="App">
      {authMethod === 'google' ? (
        <div>
          <ConfirmUploadDialog
            isOpen={showConfirmUploadDialog}
            uploadIssues={uploadIssues}
            onConfirm={() => handleConfirmUploadResponse('yes')}
            onCancel={() => handleConfirmUploadResponse('no')}
            t={t}
            tPlural={tPlural}
            formatCount={formatCount}
            haptic={haptic}
          />
          {renderMainComponent()}
        </div>
      ) : (
        <div
        className={`flex items-center justify-center h-screen ${
          version >= 2 ? 'bg-theme-v2-clip-bg' : 'bg-theme-v1-clip-bg'
        }`}>
          <div className='cardContainer flex flex-col w-full max-w-[800px] mx-auto h-full' ref={messagesRef}>
            <UploadStatusList 
              uploadStatus={uploadStatus}
              cameraDetails={cameraDetails}
              showGalleryMessage={false}
              version={version}
              t={t}
              tPlural={tPlural}
              formatCount={formatCount}
            />
            <input 
              multiple 
              type="file" 
              accept="image/*,.heic,.heif" 
              name="image" 
              id="file-input" 
              onChange={handleFileChange} 
              ref={fileInputRef} 
              className="hidden"
            />
            <label htmlFor="file-input" className="cursor-pointer">
              <div className={outlineClassName}>
                <button
                  type="button"
                  className={buttonClassName}
                  onClick={() => { haptic(); handleButtonClick(); }}
                >
                  {buttonText}
                </button>
              </div>
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
